import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { playSound, SOUNDS } from '../App';
import { Toaster } from './ui/sonner';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

// ============================================
// AUTHENTIC WINDOWS 98 COLOR PALETTE
// ============================================
const WIN98 = {
  // Desktop
  desktop: '#3A6EA5',
  
  // Window colors
  activeTitleBar: 'linear-gradient(90deg, #0A246A 0%, #A6CAF0 100%)',
  inactiveTitleBar: 'linear-gradient(90deg, #808080 0%, #C0C0C0 100%)',
  activeTitleText: '#FFFFFF',
  inactiveTitleText: '#C0C0C0',
  
  // 3D Border colors (for raised elements)
  borderLight: '#FFFFFF',
  borderMid: '#C0C0C0',
  borderDark: '#808080',
  borderDarker: '#000000',
  
  // Window body
  windowFace: '#C0C0C0',
  windowBackground: '#FFFFFF',
  
  // Taskbar
  taskbar: '#C0C0C0',
  
  // Start button
  startGreen: '#008000',
  
  // Selection
  selection: '#0A246A',
  selectionText: '#FFFFFF',
  
  // Menu
  menuHighlight: '#0A246A',
};

// ============================================
// AUTHENTIC WIN98 32x32 ICONS (Pixel-perfect SVG)
// ============================================
const Win98Icon = {
  // My Computer - Authentic beige CRT with keyboard
  MyComputer: () => (
    <svg width="32" height="32" viewBox="0 0 32 32" style={{ imageRendering: 'pixelated' }}>
      {/* Monitor body */}
      <rect x="3" y="2" width="26" height="18" fill="#C0C0C0"/>
      <rect x="3" y="2" width="26" height="1" fill="#DFDFDF"/>
      <rect x="3" y="2" width="1" height="18" fill="#DFDFDF"/>
      <rect x="28" y="3" width="1" height="17" fill="#808080"/>
      <rect x="4" y="19" width="25" height="1" fill="#808080"/>
      
      {/* Screen bezel */}
      <rect x="5" y="4" width="22" height="14" fill="#000080"/>
      
      {/* Screen (teal) */}
      <rect x="6" y="5" width="20" height="12" fill="#008080"/>
      
      {/* Windows logo on screen */}
      <rect x="11" y="7" width="4" height="4" fill="#FF0000"/>
      <rect x="16" y="7" width="4" height="4" fill="#00FF00"/>
      <rect x="11" y="12" width="4" height="4" fill="#0000FF"/>
      <rect x="16" y="12" width="4" height="4" fill="#FFFF00"/>
      
      {/* Monitor stand */}
      <rect x="13" y="20" width="6" height="2" fill="#808080"/>
      
      {/* Monitor base */}
      <rect x="9" y="22" width="14" height="3" fill="#C0C0C0"/>
      <rect x="9" y="22" width="14" height="1" fill="#DFDFDF"/>
      <rect x="22" y="23" width="1" height="2" fill="#808080"/>
      
      {/* Keyboard */}
      <rect x="4" y="26" width="24" height="5" fill="#C0C0C0"/>
      <rect x="4" y="26" width="24" height="1" fill="#DFDFDF"/>
      <rect x="4" y="26" width="1" height="5" fill="#DFDFDF"/>
      <rect x="27" y="27" width="1" height="4" fill="#808080"/>
      <rect x="5" y="30" width="23" height="1" fill="#808080"/>
      {/* Keys */}
      <rect x="6" y="28" width="20" height="2" fill="#000000"/>
    </svg>
  ),

  // Internet Explorer - Blue 'e' with orbit
  Internet: () => (
    <svg width="32" height="32" viewBox="0 0 32 32" style={{ imageRendering: 'pixelated' }}>
      {/* Globe/Earth */}
      <circle cx="16" cy="16" r="12" fill="#0066CC"/>
      <ellipse cx="16" cy="16" rx="12" ry="4" fill="none" stroke="#FFFFFF" strokeWidth="1"/>
      <ellipse cx="16" cy="16" rx="4" ry="12" fill="none" stroke="#FFFFFF" strokeWidth="1"/>
      <line x1="4" y1="16" x2="28" y2="16" stroke="#FFFFFF" strokeWidth="1"/>
      {/* Orbit ring */}
      <ellipse cx="16" cy="16" rx="14" ry="6" fill="none" stroke="#FFD700" strokeWidth="2" transform="rotate(-30 16 16)"/>
      {/* 'e' letter suggestion */}
      <circle cx="16" cy="16" r="6" fill="#0066CC"/>
      <path d="M12 16 Q16 12 20 16" stroke="#FFFFFF" strokeWidth="2" fill="none"/>
    </svg>
  ),

  // Folder - Yellow folder
  Folder: () => (
    <svg width="32" height="32" viewBox="0 0 32 32" style={{ imageRendering: 'pixelated' }}>
      {/* Folder tab */}
      <path d="M2 8 L2 6 L12 6 L14 8" fill="#FFC800"/>
      {/* Folder body - back */}
      <rect x="2" y="8" width="28" height="20" fill="#FFC800"/>
      {/* Folder body - front highlight */}
      <rect x="2" y="8" width="28" height="2" fill="#FFE066"/>
      <rect x="2" y="8" width="2" height="20" fill="#FFE066"/>
      {/* Shadow */}
      <rect x="28" y="10" width="2" height="18" fill="#CC9900"/>
      <rect x="4" y="26" width="26" height="2" fill="#CC9900"/>
    </svg>
  ),

  // Recycle Bin - Classic trash can
  RecycleBin: () => (
    <svg width="32" height="32" viewBox="0 0 32 32" style={{ imageRendering: 'pixelated' }}>
      {/* Lid */}
      <rect x="6" y="4" width="20" height="4" fill="#C0C0C0"/>
      <rect x="6" y="4" width="20" height="1" fill="#DFDFDF"/>
      <rect x="6" y="4" width="1" height="4" fill="#DFDFDF"/>
      <rect x="25" y="5" width="1" height="3" fill="#808080"/>
      
      {/* Lid handle */}
      <rect x="13" y="1" width="6" height="4" fill="#808080"/>
      <rect x="14" y="2" width="4" height="2" fill="#C0C0C0"/>
      
      {/* Bin body */}
      <path d="M8 9 L10 28 L22 28 L24 9 Z" fill="#C0C0C0"/>
      <path d="M8 9 L10 28 L11 28 L9 9 Z" fill="#DFDFDF"/>
      <path d="M23 9 L21 28 L22 28 L24 9 Z" fill="#808080"/>
      
      {/* Recycling symbol */}
      <path d="M16 13 L19 18 L17 18 L17 23 L15 23 L15 18 L13 18 Z" fill="#008000"/>
    </svg>
  ),

  // Notepad - Document with lines
  Notepad: () => (
    <svg width="32" height="32" viewBox="0 0 32 32" style={{ imageRendering: 'pixelated' }}>
      {/* Paper */}
      <rect x="6" y="2" width="20" height="28" fill="#FFFFFF"/>
      <rect x="6" y="2" width="20" height="1" fill="#FFFFFF"/>
      <rect x="6" y="2" width="1" height="28" fill="#FFFFFF"/>
      <rect x="25" y="3" width="1" height="27" fill="#808080"/>
      <rect x="7" y="29" width="19" height="1" fill="#808080"/>
      
      {/* Blue header bar */}
      <rect x="6" y="2" width="20" height="4" fill="#000080"/>
      
      {/* Text lines */}
      <rect x="9" y="9" width="14" height="1" fill="#000000"/>
      <rect x="9" y="12" width="12" height="1" fill="#808080"/>
      <rect x="9" y="15" width="14" height="1" fill="#808080"/>
      <rect x="9" y="18" width="10" height="1" fill="#808080"/>
      <rect x="9" y="21" width="13" height="1" fill="#808080"/>
      <rect x="9" y="24" width="11" height="1" fill="#808080"/>
    </svg>
  ),

  // Star (Welcome)
  Star: () => (
    <svg width="32" height="32" viewBox="0 0 32 32" style={{ imageRendering: 'pixelated' }}>
      <polygon points="16,2 20,12 30,12 22,19 25,30 16,23 7,30 10,19 2,12 12,12" fill="#FFD700"/>
      <polygon points="16,5 19,12 27,12 21,18 23,27 16,22 9,27 11,18 5,12 13,12" fill="#FFFF00"/>
      <circle cx="16" cy="15" r="3" fill="#FFFFFF"/>
    </svg>
  ),

  // Magic Wand (App Wizard)
  Wand: () => (
    <svg width="32" height="32" viewBox="0 0 32 32" style={{ imageRendering: 'pixelated' }}>
      {/* Wand stick */}
      <rect x="4" y="22" width="18" height="4" fill="#4A3728" transform="rotate(-45 13 24)"/>
      <rect x="4" y="22" width="18" height="2" fill="#6B5344" transform="rotate(-45 13 23)"/>
      
      {/* Star tip */}
      <polygon points="24,3 26,8 31,9 27,12 28,17 24,14 20,17 21,12 17,9 22,8" fill="#FFD700"/>
      <polygon points="24,5 25,8 29,9 26,11 27,15 24,13 21,15 22,11 19,9 23,8" fill="#FFFF00"/>
      <circle cx="24" cy="10" r="2" fill="#FFFFFF"/>
      
      {/* Sparkles */}
      <rect x="14" y="4" width="2" height="2" fill="#FFFF00"/>
      <rect x="28" y="14" width="2" height="2" fill="#FFFF00"/>
      <rect x="10" y="12" width="2" height="2" fill="#FFFF00"/>
    </svg>
  ),

  // Services (Gear)
  Services: () => (
    <svg width="32" height="32" viewBox="0 0 32 32" style={{ imageRendering: 'pixelated' }}>
      {/* Gear teeth */}
      <rect x="13" y="2" width="6" height="4" fill="#808080"/>
      <rect x="13" y="26" width="6" height="4" fill="#808080"/>
      <rect x="2" y="13" width="4" height="6" fill="#808080"/>
      <rect x="26" y="13" width="4" height="6" fill="#808080"/>
      
      <rect x="5" y="5" width="5" height="4" fill="#808080" transform="rotate(45 7 7)"/>
      <rect x="22" y="5" width="5" height="4" fill="#808080" transform="rotate(-45 24 7)"/>
      <rect x="5" y="23" width="5" height="4" fill="#808080" transform="rotate(-45 7 25)"/>
      <rect x="22" y="23" width="5" height="4" fill="#808080" transform="rotate(45 24 25)"/>
      
      {/* Gear body */}
      <circle cx="16" cy="16" r="9" fill="#C0C0C0"/>
      <circle cx="16" cy="16" r="8" fill="none" stroke="#DFDFDF" strokeWidth="2"/>
      <circle cx="16" cy="16" r="4" fill="#404040"/>
    </svg>
  ),

  // Users (About)
  Users: () => (
    <svg width="32" height="32" viewBox="0 0 32 32" style={{ imageRendering: 'pixelated' }}>
      {/* Person 1 */}
      <circle cx="11" cy="9" r="5" fill="#FFCC99"/>
      <circle cx="11" cy="9" r="5" fill="none" stroke="#808080" strokeWidth="1"/>
      <ellipse cx="11" cy="24" rx="7" ry="6" fill="#0000FF"/>
      
      {/* Person 2 */}
      <circle cx="21" cy="9" r="5" fill="#FFCC99"/>
      <circle cx="21" cy="9" r="5" fill="none" stroke="#808080" strokeWidth="1"/>
      <ellipse cx="21" cy="24" rx="7" ry="6" fill="#008000"/>
    </svg>
  ),

  // Mail (Contact)
  Mail: () => (
    <svg width="32" height="32" viewBox="0 0 32 32" style={{ imageRendering: 'pixelated' }}>
      {/* Envelope body */}
      <rect x="2" y="7" width="28" height="20" fill="#FFFFCC"/>
      <rect x="2" y="7" width="28" height="1" fill="#FFFFEE"/>
      <rect x="2" y="7" width="1" height="20" fill="#FFFFEE"/>
      <rect x="29" y="8" width="1" height="19" fill="#CCCC99"/>
      <rect x="3" y="26" width="27" height="1" fill="#CCCC99"/>
      
      {/* Flap */}
      <polygon points="2,7 16,18 30,7" fill="#FFFF99"/>
      <line x1="2" y1="7" x2="16" y2="18" stroke="#CCCC66" strokeWidth="1"/>
      <line x1="30" y1="7" x2="16" y2="18" stroke="#CCCC66" strokeWidth="1"/>
      
      {/* Stamp */}
      <rect x="22" y="10" width="6" height="6" fill="#FFFFFF" stroke="#808080" strokeWidth="1"/>
      <rect x="23" y="11" width="4" height="4" fill="#FF0000"/>
    </svg>
  ),

  // Chart (Statistics)
  Chart: () => (
    <svg width="32" height="32" viewBox="0 0 32 32" style={{ imageRendering: 'pixelated' }}>
      {/* Background */}
      <rect x="4" y="4" width="24" height="24" fill="#FFFFFF"/>
      <rect x="4" y="4" width="24" height="1" fill="#FFFFFF"/>
      <rect x="4" y="4" width="1" height="24" fill="#FFFFFF"/>
      <rect x="27" y="5" width="1" height="23" fill="#808080"/>
      <rect x="5" y="27" width="23" height="1" fill="#808080"/>
      
      {/* Bars */}
      <rect x="8" y="16" width="5" height="10" fill="#0000FF"/>
      <rect x="14" y="10" width="5" height="16" fill="#00FF00"/>
      <rect x="20" y="6" width="5" height="20" fill="#FF0000"/>
      
      {/* Axes */}
      <rect x="6" y="6" width="1" height="20" fill="#000000"/>
      <rect x="6" y="25" width="20" height="1" fill="#000000"/>
    </svg>
  ),

  // Video/Media Player
  MediaPlayer: () => (
    <svg width="32" height="32" viewBox="0 0 32 32" style={{ imageRendering: 'pixelated' }}>
      {/* Film strip body */}
      <rect x="4" y="2" width="24" height="28" fill="#404040"/>
      
      {/* Sprocket holes */}
      <rect x="5" y="4" width="3" height="2" fill="#FFFFFF"/>
      <rect x="5" y="9" width="3" height="2" fill="#FFFFFF"/>
      <rect x="5" y="14" width="3" height="2" fill="#FFFFFF"/>
      <rect x="5" y="19" width="3" height="2" fill="#FFFFFF"/>
      <rect x="5" y="24" width="3" height="2" fill="#FFFFFF"/>
      
      <rect x="24" y="4" width="3" height="2" fill="#FFFFFF"/>
      <rect x="24" y="9" width="3" height="2" fill="#FFFFFF"/>
      <rect x="24" y="14" width="3" height="2" fill="#FFFFFF"/>
      <rect x="24" y="19" width="3" height="2" fill="#FFFFFF"/>
      <rect x="24" y="24" width="3" height="2" fill="#FFFFFF"/>
      
      {/* Play button frame */}
      <rect x="10" y="5" width="12" height="10" fill="#000080"/>
      <polygon points="13,7 13,13 19,10" fill="#FFFFFF"/>
      
      {/* Second frame */}
      <rect x="10" y="17" width="12" height="10" fill="#808080"/>
    </svg>
  ),

  // Minesweeper
  Mine: () => (
    <svg width="32" height="32" viewBox="0 0 32 32" style={{ imageRendering: 'pixelated' }}>
      {/* Mine body */}
      <circle cx="16" cy="16" r="8" fill="#000000"/>
      
      {/* Spikes */}
      <rect x="15" y="4" width="2" height="6" fill="#000000"/>
      <rect x="15" y="22" width="2" height="6" fill="#000000"/>
      <rect x="4" y="15" width="6" height="2" fill="#000000"/>
      <rect x="22" y="15" width="6" height="2" fill="#000000"/>
      
      {/* Diagonal spikes */}
      <rect x="6" y="6" width="5" height="2" fill="#000000" transform="rotate(45 8 7)"/>
      <rect x="21" y="6" width="5" height="2" fill="#000000" transform="rotate(-45 23 7)"/>
      <rect x="6" y="24" width="5" height="2" fill="#000000" transform="rotate(-45 8 25)"/>
      <rect x="21" y="24" width="5" height="2" fill="#000000" transform="rotate(45 23 25)"/>
      
      {/* Highlight */}
      <circle cx="13" cy="13" r="3" fill="#FFFFFF"/>
    </svg>
  ),
};

// ============================================
// WIN98 STYLED COMPONENTS
// ============================================

// Authentic Win98 3D Button
const Win98Button = ({ children, onClick, disabled, pressed, style = {} }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      fontFamily: 'Tahoma, sans-serif',
      fontSize: '11px',
      padding: '4px 12px',
      background: WIN98.windowFace,
      color: disabled ? '#808080' : '#000000',
      border: 'none',
      outline: pressed ? '1px dotted #000' : 'none',
      outlineOffset: '-4px',
      boxShadow: pressed || disabled
        ? `inset 1px 1px 0 ${WIN98.borderDark}, inset -1px -1px 0 ${WIN98.borderLight}`
        : `inset 1px 1px 0 ${WIN98.borderLight}, inset -1px -1px 0 ${WIN98.borderDark}, inset 2px 2px 0 ${WIN98.borderMid}, inset -2px -2px 0 ${WIN98.borderDarker}`,
      cursor: disabled ? 'default' : 'pointer',
      ...style
    }}
  >
    {children}
  </button>
);

// Desktop Icon
const Win98DesktopIcon = ({ id, label, Icon, x, y, selected, onSelect, onOpen }) => {
  return (
    <div
      onClick={(e) => { e.stopPropagation(); onSelect(id); }}
      onDoubleClick={() => { playSound(SOUNDS.windowOpen); onOpen(id); }}
      style={{
        position: 'absolute',
        left: x,
        top: y,
        width: '70px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '2px',
        padding: '2px',
        cursor: 'pointer',
      }}
    >
      <div style={{
        width: '32px',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: selected ? WIN98.selection : 'transparent',
        padding: '2px',
      }}>
        {Icon}
      </div>
      <span style={{
        fontSize: '11px',
        fontFamily: 'Tahoma, sans-serif',
        color: '#FFFFFF',
        textAlign: 'center',
        textShadow: '1px 1px 1px #000000',
        padding: '1px 2px',
        background: selected ? WIN98.selection : 'transparent',
        wordBreak: 'break-word',
        maxWidth: '68px',
        lineHeight: '1.2',
      }}>
        {label}
      </span>
    </div>
  );
};

// Authentic Win98 Window
const Win98Window = ({ 
  id, 
  title, 
  icon,
  children, 
  isActive, 
  position, 
  size,
  onClose, 
  onMinimize, 
  onFocus,
  onDragEnd,
}) => {
  const [isMaximized, setIsMaximized] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [localPos, setLocalPos] = useState(position);

  useEffect(() => {
    setLocalPos(position);
  }, [position]);

  const handleMouseDown = (e) => {
    if (isMaximized) return;
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - localPos.x,
      y: e.clientY - localPos.y
    });
    onFocus();
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      const newPos = {
        x: e.clientX - dragOffset.x,
        y: Math.max(0, e.clientY - dragOffset.y)
      };
      setLocalPos(newPos);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      onDragEnd?.(localPos);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, localPos, onDragEnd]);

  const windowStyle = {
    position: 'fixed',
    left: isMaximized ? 0 : localPos.x,
    top: isMaximized ? 0 : localPos.y,
    width: isMaximized ? '100%' : size?.width || 400,
    height: isMaximized ? 'calc(100vh - 28px)' : size?.height || 300,
    background: WIN98.windowFace,
    border: `1px solid ${WIN98.borderDarker}`,
    boxShadow: `
      inset 1px 1px 0 ${WIN98.borderLight},
      inset -1px -1px 0 ${WIN98.borderDark}
    `,
    display: 'flex',
    flexDirection: 'column',
    zIndex: isActive ? 100 : 50,
  };

  // Title bar button style
  const titleBtnStyle = {
    width: '16px',
    height: '14px',
    background: WIN98.windowFace,
    border: 'none',
    boxShadow: `
      inset 1px 1px 0 ${WIN98.borderLight},
      inset -1px -1px 0 ${WIN98.borderDark},
      inset 2px 2px 0 ${WIN98.borderMid},
      inset -2px -2px 0 ${WIN98.borderDarker}
    `,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.1 }}
      onClick={onFocus}
      style={windowStyle}
    >
      {/* Title Bar */}
      <div
        onMouseDown={handleMouseDown}
        style={{
          background: isActive ? WIN98.activeTitleBar : WIN98.inactiveTitleBar,
          padding: '2px 3px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: isMaximized ? 'default' : 'move',
          userSelect: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
          <span style={{ fontSize: '14px', filter: 'drop-shadow(1px 1px 0 rgba(0,0,0,0.3))' }}>{icon}</span>
          <span style={{
            color: isActive ? WIN98.activeTitleText : WIN98.inactiveTitleText,
            fontSize: '11px',
            fontFamily: 'Tahoma, sans-serif',
            fontWeight: 'bold',
          }}>
            {title}
          </span>
        </div>
        <div style={{ display: 'flex', gap: '2px' }}>
          {/* Minimize */}
          <button
            onClick={(e) => { e.stopPropagation(); playSound(SOUNDS.click); onMinimize(); }}
            style={titleBtnStyle}
          >
            <svg width="8" height="7" viewBox="0 0 8 7">
              <rect x="0" y="5" width="6" height="2" fill="#000000"/>
            </svg>
          </button>
          {/* Maximize */}
          <button
            onClick={(e) => { e.stopPropagation(); setIsMaximized(!isMaximized); }}
            style={titleBtnStyle}
          >
            <svg width="9" height="9" viewBox="0 0 9 9">
              <rect x="0" y="0" width="9" height="9" fill="none" stroke="#000000" strokeWidth="1"/>
              <rect x="0" y="0" width="9" height="2" fill="#000000"/>
            </svg>
          </button>
          {/* Close */}
          <button
            onClick={(e) => { e.stopPropagation(); playSound(SOUNDS.windowClose); onClose(); }}
            style={titleBtnStyle}
          >
            <svg width="8" height="7" viewBox="0 0 8 7">
              <line x1="0" y1="0" x2="8" y2="7" stroke="#000000" strokeWidth="1.5"/>
              <line x1="8" y1="0" x2="0" y2="7" stroke="#000000" strokeWidth="1.5"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Menu Bar */}
      <div style={{
        background: WIN98.windowFace,
        padding: '2px 4px',
        borderBottom: `1px solid ${WIN98.borderDark}`,
        display: 'flex',
        gap: '4px',
      }}>
        {['Fil', 'Rediger', 'Vis', 'Hjelp'].map((menu) => (
          <span key={menu} style={{
            fontFamily: 'Tahoma, sans-serif',
            fontSize: '11px',
            padding: '1px 4px',
            cursor: 'pointer',
          }}>
            {menu}
          </span>
        ))}
      </div>

      {/* Content */}
      <div style={{
        flex: 1,
        background: '#FFFFFF',
        margin: '2px',
        border: `1px solid ${WIN98.borderDark}`,
        boxShadow: `inset 1px 1px 0 ${WIN98.borderDarker}`,
        overflow: 'auto',
      }}>
        {children}
      </div>

      {/* Status Bar */}
      <div style={{
        background: WIN98.windowFace,
        padding: '2px 4px',
        borderTop: `1px solid ${WIN98.borderLight}`,
        display: 'flex',
        gap: '4px',
      }}>
        <div style={{
          flex: 1,
          padding: '1px 4px',
          fontSize: '11px',
          fontFamily: 'Tahoma, sans-serif',
          border: `1px solid ${WIN98.borderDark}`,
          boxShadow: `inset 1px 1px 0 ${WIN98.borderDarker}`,
          background: WIN98.windowFace,
        }}>
          Klar
        </div>
      </div>
    </motion.div>
  );
};

// Win98 Start Menu
const Win98StartMenu = ({ isOpen, onClose, onOpenWindow, onShutdown }) => {
  if (!isOpen) return null;

  const menuItems = [
    { id: 'welcome', label: 'Velkommen', icon: '⭐' },
    { id: 'appwizard', label: 'App-veiviser', icon: '🧙' },
    { divider: true },
    { id: 'services', label: 'Tjenester', icon: '⚙️' },
    { id: 'metrics', label: 'Statistikk', icon: '📊' },
    { id: 'about', label: 'Om Oss', icon: '👥' },
    { id: 'contact', label: 'Kontakt', icon: '📧' },
    { divider: true },
    { id: 'video', label: 'Demo Video', icon: '🎬' },
    { id: 'minesweeper', label: 'Minesveiper', icon: '💣' },
    { divider: true },
    { id: 'help', label: 'Hjelp', icon: '❓' },
    { id: 'shutdown', label: 'Avslutt...', icon: '⏻', action: 'shutdown' },
  ];

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, zIndex: 998 }}
      />
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        transition={{ duration: 0.1 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          left: '0px',
          bottom: '28px',
          width: '180px',
          background: WIN98.windowFace,
          border: `1px solid ${WIN98.borderDarker}`,
          boxShadow: `
            inset 1px 1px 0 ${WIN98.borderLight},
            inset -1px -1px 0 ${WIN98.borderDark},
            2px 2px 4px rgba(0,0,0,0.3)
          `,
          zIndex: 999,
          display: 'flex',
        }}
      >
        {/* Windows 98 Banner */}
        <div style={{
          width: '22px',
          background: 'linear-gradient(180deg, #000080 0%, #1084d0 100%)',
          display: 'flex',
          alignItems: 'flex-end',
          padding: '4px 2px',
        }}>
          <span style={{
            writingMode: 'vertical-rl',
            transform: 'rotate(180deg)',
            color: '#FFFFFF',
            fontSize: '18px',
            fontWeight: 'bold',
            fontFamily: 'Arial, sans-serif',
            letterSpacing: '1px',
          }}>
            Windows<span style={{ fontWeight: '300', color: '#C0C0C0' }}>98</span>
          </span>
        </div>

        {/* Menu Items */}
        <div style={{ flex: 1, padding: '2px' }}>
          {menuItems.map((item, i) => (
            item.divider ? (
              <div key={i} style={{
                height: '1px',
                background: WIN98.borderDark,
                margin: '3px 2px',
                boxShadow: `0 1px 0 ${WIN98.borderLight}`,
              }} />
            ) : (
              <button
                key={item.id}
                onClick={() => { 
                  playSound(SOUNDS.click);
                  if (item.action === 'shutdown') {
                    onShutdown?.();
                  } else {
                    onOpenWindow(item.id);
                  }
                  onClose(); 
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = WIN98.menuHighlight;
                  e.currentTarget.style.color = '#FFFFFF';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#000000';
                }}
                style={{
                  width: '100%',
                  padding: '4px 6px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Tahoma, sans-serif',
                  fontSize: '11px',
                  textAlign: 'left',
                  color: '#000000',
                }}
              >
                <span style={{ fontSize: '14px', width: '18px', textAlign: 'center' }}>{item.icon}</span>
                <span>{item.label}</span>
              </button>
            )
          ))}
        </div>
      </motion.div>
    </>
  );
};

// Win98 Taskbar
const Win98Taskbar = ({ 
  openWindows, 
  activeWindow, 
  onStartClick, 
  startMenuOpen,
  onWindowClick,
}) => {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      height: '28px',
      background: WIN98.taskbar,
      borderTop: `1px solid ${WIN98.borderLight}`,
      display: 'flex',
      alignItems: 'center',
      padding: '2px',
      zIndex: 1000,
    }}>
      {/* Start Button */}
      <button
        onClick={(e) => { e.stopPropagation(); onStartClick(); }}
        data-testid="win98-start-button"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '3px',
          padding: '2px 6px',
          height: '22px',
          background: WIN98.windowFace,
          border: 'none',
          boxShadow: startMenuOpen 
            ? `inset 1px 1px 0 ${WIN98.borderDark}, inset -1px -1px 0 ${WIN98.borderLight}`
            : `inset 1px 1px 0 ${WIN98.borderLight}, inset -1px -1px 0 ${WIN98.borderDark}, inset 2px 2px 0 ${WIN98.borderMid}, inset -2px -2px 0 ${WIN98.borderDarker}`,
          cursor: 'pointer',
          fontFamily: 'Tahoma, sans-serif',
          fontSize: '11px',
          fontWeight: 'bold',
        }}
      >
        {/* Windows Flag */}
        <svg width="16" height="14" viewBox="0 0 16 14">
          <rect x="1" y="1" width="6" height="5" fill="#FF0000"/>
          <rect x="8" y="1" width="6" height="5" fill="#00FF00"/>
          <rect x="1" y="7" width="6" height="5" fill="#0000FF"/>
          <rect x="8" y="7" width="6" height="5" fill="#FFFF00"/>
        </svg>
        Start
      </button>

      {/* Divider */}
      <div style={{
        width: '2px',
        height: '20px',
        margin: '0 2px',
        borderLeft: `1px solid ${WIN98.borderDark}`,
        borderRight: `1px solid ${WIN98.borderLight}`,
      }} />

      {/* Quick Launch */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '1px',
        padding: '0 4px',
        borderRight: `1px solid ${WIN98.borderDark}`,
        marginRight: '4px',
      }}>
        {['🖥️', '🌐', '📁'].map((icon, i) => (
          <button key={i} style={{
            width: '20px',
            height: '20px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {icon}
          </button>
        ))}
      </div>

      {/* Window Buttons */}
      <div style={{ flex: 1, display: 'flex', gap: '2px', overflow: 'hidden' }}>
        {Object.entries(openWindows).filter(([_, w]) => w.open).map(([id, window]) => (
          <button
            key={id}
            onClick={() => onWindowClick(id)}
            style={{
              flex: '0 1 140px',
              minWidth: '60px',
              height: '22px',
              padding: '2px 6px',
              background: WIN98.windowFace,
              border: 'none',
              boxShadow: activeWindow === id
                ? `inset 1px 1px 0 ${WIN98.borderDark}, inset -1px -1px 0 ${WIN98.borderLight}`
                : `inset 1px 1px 0 ${WIN98.borderLight}, inset -1px -1px 0 ${WIN98.borderDark}`,
              cursor: 'pointer',
              fontFamily: 'Tahoma, sans-serif',
              fontSize: '11px',
              textAlign: 'left',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <span style={{ fontSize: '12px' }}>{window.icon || '📁'}</span>
            <span>{window.title || id}</span>
          </button>
        ))}
      </div>

      {/* System Tray */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        padding: '2px 6px',
        height: '20px',
        borderLeft: `1px solid ${WIN98.borderDark}`,
        borderTop: `1px solid ${WIN98.borderDark}`,
        borderRight: `1px solid ${WIN98.borderLight}`,
        borderBottom: `1px solid ${WIN98.borderLight}`,
        background: WIN98.windowFace,
      }}>
        <span style={{ fontSize: '12px' }}>🔊</span>
        <span style={{
          fontFamily: 'Tahoma, sans-serif',
          fontSize: '11px',
        }}>
          {time.toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
    </div>
  );
};

// ============================================
// WINDOW CONTENT COMPONENTS
// ============================================

// Welcome Content
const WelcomeContent = ({ onOpenContact, onOpenServices, onClose }) => (
  <div style={{ padding: '16px', fontFamily: 'Tahoma, sans-serif', fontSize: '11px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
      <div style={{ width: '40px', height: '40px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px' }}>
        <div style={{ background: '#FF0000' }} />
        <div style={{ background: '#00FF00' }} />
        <div style={{ background: '#0000FF' }} />
        <div style={{ background: '#FFFF00' }} />
      </div>
      <div>
        <h1 style={{ margin: 0, fontSize: '16px', color: '#000080' }}>Velkommen til Autonomous</h1>
        <p style={{ margin: '4px 0 0 0', color: '#666' }}>Windows 98 Edition</p>
      </div>
    </div>

    <div style={{ background: '#FFFFCC', border: '1px solid #CCCC99', padding: '8px', marginBottom: '16px' }}>
      <strong>💡 Tips:</strong> Dobbeltklikk på ikonene for å utforske!
    </div>

    <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
      {[
        { num: '01', value: '6 uker', label: 'til produksjon' },
        { num: '02', value: '99.7%', label: 'oppetid garantert' },
        { num: '03', value: '0', label: 'feilede piloter' }
      ].map((stat, i) => (
        <div key={i} style={{ 
          flex: 1, padding: '8px', background: '#F0F0F0', 
          border: `1px solid ${WIN98.borderDark}`,
          boxShadow: `inset 1px 1px 0 ${WIN98.borderDarker}`
        }}>
          <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#000080' }}>{stat.value}</div>
          <div style={{ fontSize: '10px', color: '#666' }}>{stat.label}</div>
        </div>
      ))}
    </div>

    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      {[
        { icon: '🎬', label: 'Se en demo', id: 'video' },
        { icon: '💼', label: 'Utforsk tjenester', id: 'services' },
        { icon: '🧙', label: 'Prøv App-veiviseren', id: 'appwizard' },
        { icon: '📧', label: 'Kontakt oss', id: 'contact' },
      ].map((item) => (
        <button
          key={item.id}
          onClick={() => {
            playSound(SOUNDS.click);
            if (item.id === 'contact') onOpenContact?.();
            else if (item.id === 'services') onOpenServices?.();
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#E0E8F0'}
          onMouseLeave={(e) => e.currentTarget.style.background = '#F0F4F8'}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px', padding: '8px',
            background: '#F0F4F8', border: `1px solid ${WIN98.borderDark}`,
            cursor: 'pointer', textAlign: 'left', fontFamily: 'Tahoma, sans-serif', fontSize: '11px'
          }}
        >
          <span style={{ fontSize: '18px' }}>{item.icon}</span>
          <span style={{ fontWeight: 'bold', color: '#000080' }}>{item.label}</span>
          <span style={{ marginLeft: 'auto', color: '#808080' }}>▶</span>
        </button>
      ))}
    </div>
  </div>
);

// Contact Form
const ContactContent = () => {
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', company: '', phone: '', projectDetails: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    // try {
  //     const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify({
  //         first_name: formData.firstName, last_name: formData.lastName, email: formData.email,
  //         company: formData.company, phone: formData.phone, project_details: formData.projectDetails
  //       })
  //     });
  //     if (response.ok) {
  //       toast.success('Melding sendt!');
  //       confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
  //       setFormData({ firstName: '', lastName: '', email: '', company: '', phone: '', projectDetails: '' });
  //     } else {
  //       toast.error('Noe gikk galt.');
  //     }
  //   } catch (error) {
  //     toast.error('Kunne ikke sende.');
  //   }
  //   setIsSubmitting(false);
    try {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        company: formData.company,
        phone: formData.phone,
        project_details: formData.projectDetails
      })
    });
    
    // Read response body first, then parse
    const text = await response.text();
    let data;
    try {
      data = JSON.parse(text);
    } catch (parseError) {
      console.error('Failed to parse response:', text);
      throw new Error('Serverfeil. Prøv igjen senere.');
    }
    
    if (!response.ok) {
      throw new Error(data.detail || 'Noe gikk galt');
    }
    
    toast.success('Melding sendt!');
    confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
    setFormData({ firstName: '', lastName: '', email: '', company: '', phone: '', projectDetails: '' });
  } catch (error) {
    toast.error(error.message || 'Kunne ikke sende.');
  }
  };

  const inputStyle = {
    width: '100%', padding: '4px', fontFamily: 'Tahoma, sans-serif', fontSize: '11px',
    border: `1px solid ${WIN98.borderDark}`, boxShadow: `inset 1px 1px 0 ${WIN98.borderDarker}`,
    boxSizing: 'border-box'
  };

  return (
    <div style={{ padding: '12px', fontFamily: 'Tahoma, sans-serif', fontSize: '11px' }}>
      <h2 style={{ color: '#000080', margin: '0 0 8px 0', fontSize: '13px' }}>Kontakt Oss</h2>
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
          <div>
            <label>Fornavn *</label>
            <input style={inputStyle} value={formData.firstName} onChange={(e) => setFormData({...formData, firstName: e.target.value})} required />
          </div>
          <div>
            <label>Etternavn *</label>
            <input style={inputStyle} value={formData.lastName} onChange={(e) => setFormData({...formData, lastName: e.target.value})} required />
          </div>
        </div>
        <div style={{ marginBottom: '8px' }}>
          <label>E-post *</label>
          <input type="email" style={inputStyle} value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
          <div>
            <label>Selskap</label>
            <input style={inputStyle} value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} />
          </div>
          <div>
            <label>Telefon</label>
            <input style={inputStyle} value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
          </div>
        </div>
        <div style={{ marginBottom: '12px' }}>
          <label>Prosjektdetaljer *</label>
          <textarea style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }} value={formData.projectDetails} onChange={(e) => setFormData({...formData, projectDetails: e.target.value})} required />
        </div>
        <Win98Button onClick={handleSubmit} disabled={isSubmitting} style={{ width: '100%', padding: '6px' }}>
          {isSubmitting ? 'Sender...' : 'Send Melding'}
        </Win98Button>
      </form>
    </div>
  );
};

// Services Content
const ServicesContent = () => (
  <div style={{ padding: '12px', fontFamily: 'Tahoma, sans-serif', fontSize: '11px' }}>
    <h2 style={{ color: '#000080', margin: '0 0 12px 0', fontSize: '13px' }}>Våre Tjenester</h2>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
      {[
        { icon: '🤖', title: 'Agentiske AI-systemer', desc: 'Autonome agenter som utfører oppgaver.' },
        { icon: '🎯', title: 'AI-strategi', desc: 'Fra idé til produksjon på 90 dager.' },
        { icon: '⚡', title: 'Prosessautomatisering', desc: 'Intelligente arbeidsflyter som skalerer.' },
        { icon: '📊', title: 'Dataintelligens', desc: 'Innsikt som driver beslutninger.' },
      ].map((s, i) => (
        <div key={i} style={{ padding: '10px', background: '#F5F5F5', border: `1px solid ${WIN98.borderDark}` }}>
          <div style={{ fontSize: '20px', marginBottom: '6px' }}>{s.icon}</div>
          <strong style={{ color: '#000080' }}>{s.title}</strong>
          <p style={{ margin: '4px 0 0 0', color: '#555' }}>{s.desc}</p>
        </div>
      ))}
    </div>
  </div>
);

// About Content  
const AboutContent = () => (
  <div style={{ padding: '12px', fontFamily: 'Tahoma, sans-serif', fontSize: '11px' }}>
    <h2 style={{ color: '#000080', margin: '0 0 8px 0', fontSize: '13px' }}>Om Autonomous</h2>
    <p style={{ lineHeight: 1.5, marginBottom: '12px' }}>
      Vi er ingeniører som ble lei av å se AI-prosjekter feile. Så vi bygget et selskap som gjør én ting: <strong>få AI i produksjon.</strong>
    </p>
    <div style={{ background: '#F0F4FF', border: '1px solid #B8C5E8', padding: '10px' }}>
      <h3 style={{ color: '#000080', margin: '0 0 6px 0', fontSize: '12px' }}>Vår Filosofi</h3>
      <ul style={{ margin: 0, paddingLeft: '16px', lineHeight: 1.6 }}>
        <li>Vi sier hva vi skal gjøre, så gjør vi det.</li>
        <li>Vi eier resultatet — 99.7% oppetid garantert.</li>
        <li>Vi leverer raskt — uker, ikke kvartaler.</li>
      </ul>
    </div>
  </div>
);

// Metrics Content
const MetricsContent = () => (
  <div style={{ padding: '12px', fontFamily: 'Tahoma, sans-serif', fontSize: '11px' }}>
    <h2 style={{ color: '#000080', margin: '0 0 12px 0', fontSize: '13px' }}>Statistikk</h2>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
      {[
        { value: '50+', label: 'Prosjekter' },
        { value: '99.7%', label: 'Oppetid' },
        { value: '6 uker', label: 'Leveringstid' },
        { value: '0', label: 'Feilede piloter' },
        { value: '24/7', label: 'Support' },
        { value: '100%', label: 'Fornøyde' },
      ].map((s, i) => (
        <div key={i} style={{ padding: '12px', background: '#F0F0F0', border: `1px solid ${WIN98.borderDark}`, textAlign: 'center' }}>
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#000080' }}>{s.value}</div>
          <div style={{ fontSize: '10px', color: '#666' }}>{s.label}</div>
        </div>
      ))}
    </div>
  </div>
);

// Minesweeper
const MinesweeperContent = () => {
  const [grid] = useState(() => {
    const g = [];
    for (let i = 0; i < 8; i++) {
      g.push(Array(8).fill(null).map(() => Math.random() < 0.15 ? '💣' : ''));
    }
    return g;
  });
  const [revealed, setRevealed] = useState(() => Array(8).fill(null).map(() => Array(8).fill(false)));
  const [gameOver, setGameOver] = useState(false);

  const countMines = (row, col) => {
    let count = 0;
    for (let r = row - 1; r <= row + 1; r++) {
      for (let c = col - 1; c <= col + 1; c++) {
        if (r >= 0 && r < 8 && c >= 0 && c < 8 && grid[r][c] === '💣') count++;
      }
    }
    return count;
  };

  const handleClick = (row, col) => {
    if (gameOver || revealed[row][col]) return;
    const newRevealed = revealed.map(r => [...r]);
    newRevealed[row][col] = true;
    setRevealed(newRevealed);
    if (grid[row][col] === '💣') {
      setGameOver(true);
      toast.error('BOOM! 💥');
    }
  };

  return (
    <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(8, 24px)',
        gap: '1px',
        background: WIN98.borderDark,
        padding: '3px',
        border: `2px solid`,
        borderColor: `${WIN98.borderDark} ${WIN98.borderLight} ${WIN98.borderLight} ${WIN98.borderDark}`,
      }}>
        {grid.map((row, r) => row.map((cell, c) => (
          <button
            key={`${r}-${c}`}
            onClick={() => handleClick(r, c)}
            style={{
              width: '24px',
              height: '24px',
              background: revealed[r][c] ? WIN98.windowFace : WIN98.windowFace,
              border: 'none',
              boxShadow: revealed[r][c] 
                ? 'none'
                : `inset 1px 1px 0 ${WIN98.borderLight}, inset -1px -1px 0 ${WIN98.borderDark}, inset 2px 2px 0 #FFFFFF, inset -2px -2px 0 ${WIN98.borderDarker}`,
              cursor: gameOver ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: 'bold',
              fontFamily: 'Arial, sans-serif',
              color: ['', '#0000FF', '#008000', '#FF0000', '#000080', '#800000', '#008080', '#000000', '#808080'][countMines(r, c)] || '#000000',
            }}
          >
            {revealed[r][c] && (cell === '💣' ? '💣' : (countMines(r, c) || ''))}
          </button>
        )))}
      </div>
      {gameOver && (
        <Win98Button onClick={() => window.location.reload()} style={{ marginTop: '8px' }}>
          Nytt spill
        </Win98Button>
      )}
    </div>
  );
};

// Video Content
const VideoContent = () => (
  <div style={{ padding: '8px' }}>
    <video src="/demo_video.mp4" controls style={{ width: '100%', background: '#000' }} />
  </div>
);

// App Wizard Content
const AppWizardContent = ({ onOpenContact, onClose }) => {
  const [step, setStep] = useState(1);
  const [platform, setPlatform] = useState('');
  const [appType, setAppType] = useState('');
  const [features, setFeatures] = useState([]);
  const [building, setBuilding] = useState(false);
  const [done, setDone] = useState(false);

  const platforms = [{ id: 'desktop', name: 'Desktop', icon: '🖥️' }, { id: 'mobile', name: 'Mobil', icon: '📱' }, { id: 'web', name: 'Web', icon: '🌐' }];
  const appTypes = {
    desktop: [{ id: 'dashboard', name: 'Dashboard', icon: '📊' }, { id: 'kanban', name: 'Prosjekt', icon: '📋' }],
    mobile: [{ id: 'fitness', name: 'Trening', icon: '💪' }, { id: 'chat', name: 'Chat', icon: '💬' }],
    web: [{ id: 'ecommerce', name: 'Nettbutikk', icon: '🛒' }, { id: 'saas', name: 'SaaS', icon: '☁️' }]
  };
  const featuresList = [{ id: 'ai', name: 'AI', icon: '🤖' }, { id: 'auth', name: 'Innlogging', icon: '🔐' }, { id: 'pay', name: 'Betaling', icon: '💳' }, { id: 'notify', name: 'Varsler', icon: '🔔' }];

  if (building) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'Tahoma, sans-serif' }}>
        <div style={{ fontSize: '40px', marginBottom: '16px' }}>⏳</div>
        <h3 style={{ color: '#000080' }}>Bygger app...</h3>
        <div style={{ width: '200px', height: '16px', background: '#FFFFFF', border: `1px solid ${WIN98.borderDark}`, margin: '16px auto', boxShadow: `inset 1px 1px 0 ${WIN98.borderDarker}` }}>
          <motion.div
            animate={{ width: ['0%', '100%'] }}
            transition={{ duration: 2 }}
            onAnimationComplete={() => { setBuilding(false); setDone(true); confetti({ particleCount: 100, spread: 70 }); }}
            style={{ height: '100%', background: '#000080' }}
          />
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div style={{ padding: '30px', textAlign: 'center', fontFamily: 'Tahoma, sans-serif', fontSize: '11px' }}>
        <div style={{ fontSize: '50px', marginBottom: '12px' }}>🎉</div>
        <h2 style={{ color: '#000080' }}>Appen er klar!</h2>
        <p style={{ marginBottom: '16px' }}>Kontakt oss for å bygge den på ordentlig.</p>
        <Win98Button onClick={() => { onOpenContact?.(); onClose?.(); }}>
          Kontakt oss →
        </Win98Button>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px', fontFamily: 'Tahoma, sans-serif', fontSize: '11px' }}>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '16px' }}>
        {[1, 2, 3].map(s => (
          <div key={s} style={{
            width: '24px', height: '24px', borderRadius: '50%',
            background: step >= s ? '#000080' : WIN98.windowFace,
            color: step >= s ? '#FFF' : '#666',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `1px solid ${WIN98.borderDark}`, fontSize: '10px', fontWeight: 'bold'
          }}>{s}</div>
        ))}
      </div>

      {step === 1 && (
        <div>
          <h3 style={{ color: '#000080', marginBottom: '12px' }}>Velg plattform</h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            {platforms.map(p => (
              <button key={p.id} onClick={() => { setPlatform(p.id); setStep(2); }}
                style={{ flex: 1, padding: '16px', background: '#F5F5F5', border: `1px solid ${WIN98.borderDark}`, cursor: 'pointer', textAlign: 'center' }}>
                <div style={{ fontSize: '28px' }}>{p.icon}</div>
                <div style={{ fontWeight: 'bold', marginTop: '4px' }}>{p.name}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          <h3 style={{ color: '#000080', marginBottom: '12px' }}>Velg app-type</h3>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            {appTypes[platform]?.map(t => (
              <button key={t.id} onClick={() => { setAppType(t.id); setStep(3); }}
                style={{ flex: 1, padding: '16px', background: '#F5F5F5', border: `1px solid ${WIN98.borderDark}`, cursor: 'pointer', textAlign: 'center' }}>
                <div style={{ fontSize: '28px' }}>{t.icon}</div>
                <div style={{ fontWeight: 'bold', marginTop: '4px' }}>{t.name}</div>
              </button>
            ))}
          </div>
          <Win98Button onClick={() => setStep(1)}>← Tilbake</Win98Button>
        </div>
      )}

      {step === 3 && (
        <div>
          <h3 style={{ color: '#000080', marginBottom: '12px' }}>Velg funksjoner</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '12px' }}>
            {featuresList.map(f => (
              <button key={f.id}
                onClick={() => setFeatures(prev => prev.includes(f.id) ? prev.filter(x => x !== f.id) : [...prev, f.id])}
                style={{
                  padding: '10px', background: features.includes(f.id) ? '#E0E8FF' : '#F5F5F5',
                  border: features.includes(f.id) ? '2px solid #000080' : `1px solid ${WIN98.borderDark}`,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
                }}>
                <span>{f.icon}</span><span>{f.name}</span>
                {features.includes(f.id) && <span style={{ marginLeft: 'auto' }}>✓</span>}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Win98Button onClick={() => setStep(2)}>← Tilbake</Win98Button>
            <Win98Button onClick={() => setBuilding(true)} disabled={features.length === 0} style={{ flex: 1 }}>
              Bygg app →
            </Win98Button>
          </div>
        </div>
      )}
    </div>
  );
};

// Help Content
const HelpContent = () => (
  <div style={{ padding: '12px', fontFamily: 'Tahoma, sans-serif', fontSize: '11px' }}>
    <h2 style={{ color: '#000080', margin: '0 0 12px 0', fontSize: '13px' }}>Hjelp</h2>
    <h3 style={{ fontSize: '12px', marginBottom: '6px' }}>Navigering</h3>
    <ul style={{ paddingLeft: '16px', marginBottom: '12px', lineHeight: 1.6 }}>
      <li><strong>Dobbeltklikk</strong> — Åpne program</li>
      <li><strong>Klikk</strong> — Velg ikon</li>
      <li><strong>Start-knapp</strong> — Åpne menyen</li>
    </ul>
    <div style={{ background: '#FFFFCC', border: '1px solid #CCCC99', padding: '8px' }}>
      <strong>💡 Tips:</strong> Prøv App-veiviseren!
    </div>
  </div>
);

// ============================================
// WIN98 BOOT SEQUENCE
// ============================================
const Win98BootSequence = ({ onComplete }) => {
  const [phase, setPhase] = useState(0);
  // 0: black, 1: BIOS, 2: Windows logo, 3: loading bar, 4: done

  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase(1), 300),
      setTimeout(() => setPhase(2), 1500),
      setTimeout(() => setPhase(3), 2500),
      setTimeout(() => setPhase(4), 4500),
      setTimeout(() => onComplete(), 5000),
    ];
    return () => timers.forEach(clearTimeout);
  }, [onComplete]);

  // Phase 0-1: BIOS screen
  if (phase <= 1) {
    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        background: '#000',
        fontFamily: 'Consolas, monospace',
        color: '#AAAAAA',
        fontSize: '14px',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {phase >= 1 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div style={{ color: '#FFFFFF', marginBottom: '4px' }}>Award Modular BIOS v4.51PG</div>
            <div style={{ marginBottom: '20px' }}>Copyright (C) 1984-98 Award Software, Inc.</div>
            <div>Autonomous AI Systems Inc.</div>
            <div style={{ marginTop: '20px' }}>
              <span style={{ color: '#FFFF00' }}>CPU: </span>
              <span>Pentium II 450MHz</span>
            </div>
            <div>
              <span style={{ color: '#FFFF00' }}>Memory Test: </span>
              <motion.span
                animate={{ opacity: [1, 0.5, 1] }}
                transition={{ duration: 0.5, repeat: 2 }}
              >
                131072K OK
              </motion.span>
            </div>
            <div style={{ marginTop: '20px', color: '#FFFFFF' }}>
              Press DEL to enter SETUP
            </div>
          </motion.div>
        )}
      </div>
    );
  }

  // Phase 2-4: Windows 98 logo and loading
  return (
    <div style={{
      width: '100vw',
      height: '100vh',
      background: '#000',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      {/* Windows 98 Logo */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        style={{ marginBottom: '40px', textAlign: 'center' }}
      >
        {/* Windows flag */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '4px',
          width: '80px',
          height: '80px',
          margin: '0 auto 20px',
        }}>
          <motion.div 
            animate={{ opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 1.5, repeat: Infinity, delay: 0 }}
            style={{ background: '#FF0000', borderRadius: '4px 0 0 0' }} 
          />
          <motion.div 
            animate={{ opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 1.5, repeat: Infinity, delay: 0.2 }}
            style={{ background: '#00FF00', borderRadius: '0 4px 0 0' }} 
          />
          <motion.div 
            animate={{ opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 1.5, repeat: Infinity, delay: 0.4 }}
            style={{ background: '#0000FF', borderRadius: '0 0 0 4px' }} 
          />
          <motion.div 
            animate={{ opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 1.5, repeat: Infinity, delay: 0.6 }}
            style={{ background: '#FFFF00', borderRadius: '0 0 4px 0' }} 
          />
        </div>
        
        <div style={{
          fontFamily: 'Arial, sans-serif',
          fontSize: '32px',
          fontWeight: 'bold',
          color: '#FFFFFF',
          letterSpacing: '-1px',
        }}>
          Microsoft<span style={{ fontWeight: '300', position: 'relative', top: '-10px', fontSize: '14px' }}>®</span>
        </div>
        <div style={{
          fontFamily: 'Arial, sans-serif',
          fontSize: '42px',
          fontWeight: 'bold',
          color: '#FFFFFF',
          marginTop: '-8px',
        }}>
          Windows<span style={{ color: '#C0C0C0', fontWeight: '300' }}>98</span>
        </div>
      </motion.div>

      {/* Loading bar */}
      {phase >= 3 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{
            width: '200px',
            height: '18px',
            background: '#000',
            border: '1px solid #808080',
            padding: '2px',
          }}
        >
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: '100%' }}
            transition={{ duration: 2, ease: 'linear' }}
            style={{
              height: '100%',
              background: 'linear-gradient(90deg, #0000AA, #0000FF, #0000AA)',
              display: 'flex',
              gap: '2px',
            }}
          >
            {[...Array(20)].map((_, i) => (
              <div key={i} style={{
                flex: 1,
                background: i % 2 === 0 ? '#0000FF' : '#0000AA',
              }} />
            ))}
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
export default function Win98Desktop() {
  const [isBooting, setIsBooting] = useState(true);
  const [selectedIcon, setSelectedIcon] = useState(null);
  const [startMenuOpen, setStartMenuOpen] = useState(false);
  const [windows, setWindows] = useState({
    welcome: { open: true, minimized: false, title: 'Velkommen', icon: '⭐', position: { x: 120, y: 50 }, size: { width: 420, height: 380 } }
  });
  const [activeWindow, setActiveWindow] = useState('welcome');
  const [isShutdown, setIsShutdown] = useState(false);

  const desktopIcons = [
    { id: 'mycomputer', label: 'Min datamaskin', Icon: <Win98Icon.MyComputer />, x: 16, y: 16 },
    { id: 'internet', label: 'Internett', Icon: <Win98Icon.Internet />, x: 16, y: 90 },
    { id: 'documents', label: 'Mine dokumenter', Icon: <Win98Icon.Folder />, x: 16, y: 164 },
    { id: 'recycle', label: 'Papirkurv', Icon: <Win98Icon.RecycleBin />, x: 16, y: 238 },
    { id: 'welcome', label: 'Velkommen', Icon: <Win98Icon.Star />, x: 16, y: 312 },
    { id: 'appwizard', label: 'App-veiviser', Icon: <Win98Icon.Wand />, x: 16, y: 386 },
    { id: 'services', label: 'Tjenester', Icon: <Win98Icon.Services />, x: 90, y: 16 },
    { id: 'metrics', label: 'Statistikk', Icon: <Win98Icon.Chart />, x: 90, y: 90 },
    { id: 'about', label: 'Om oss', Icon: <Win98Icon.Users />, x: 90, y: 164 },
    { id: 'contact', label: 'Kontakt', Icon: <Win98Icon.Mail />, x: 90, y: 238 },
    { id: 'video', label: 'Demo Video', Icon: <Win98Icon.MediaPlayer />, x: 90, y: 312 },
    { id: 'minesweeper', label: 'Minesveiper', Icon: <Win98Icon.Mine />, x: 90, y: 386 },
  ];

  const windowConfigs = {
    welcome: { title: 'Velkommen', icon: '⭐', size: { width: 420, height: 380 } },
    services: { title: 'Tjenester', icon: '⚙️', size: { width: 440, height: 360 } },
    about: { title: 'Om oss', icon: '👥', size: { width: 400, height: 320 } },
    contact: { title: 'Kontakt', icon: '📧', size: { width: 380, height: 420 } },
    metrics: { title: 'Statistikk', icon: '📊', size: { width: 420, height: 340 } },
    appwizard: { title: 'App-veiviser', icon: '🧙', size: { width: 380, height: 360 } },
    video: { title: 'Demo Video', icon: '🎬', size: { width: 500, height: 360 } },
    minesweeper: { title: 'Minesveiper', icon: '💣', size: { width: 260, height: 320 } },
    help: { title: 'Hjelp', icon: '❓', size: { width: 340, height: 300 } },
  };

  const openWindow = (id) => {
    const config = windowConfigs[id] || { title: id, icon: '📁', size: { width: 400, height: 300 } };
    const existingWindow = windows[id];
    setWindows(prev => ({
      ...prev,
      [id]: {
        open: true, minimized: false, ...config,
        position: existingWindow?.position || { x: 120 + Object.keys(prev).length * 25, y: 50 + Object.keys(prev).length * 25 }
      }
    }));
    setActiveWindow(id);
  };

  const closeWindow = (id) => setWindows(prev => ({ ...prev, [id]: { ...prev[id], open: false } }));
  const minimizeWindow = (id) => setWindows(prev => ({ ...prev, [id]: { ...prev[id], minimized: true } }));
  const focusWindow = (id) => { setActiveWindow(id); setWindows(prev => ({ ...prev, [id]: { ...prev[id], minimized: false } })); };

  // Show boot sequence
  if (isBooting) {
    return <Win98BootSequence onComplete={() => setIsBooting(false)} />;
  }

  if (isShutdown) {
    return (
      <div style={{ width: '100vw', height: '100vh', background: '#000', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#FF8C00', fontFamily: 'Tahoma, sans-serif' }}>
        <h1 style={{ fontSize: '20px', marginBottom: '20px' }}>Det er nå trygt å skru av datamaskinen.</h1>
        <Win98Button onClick={() => setIsShutdown(false)}>Start på nytt</Win98Button>
      </div>
    );
  }

  return (
    <div
      data-testid="win98-desktop"
      style={{ width: '100vw', height: '100vh', background: WIN98.desktop, position: 'relative', overflow: 'hidden' }}
      onClick={() => { setSelectedIcon(null); setStartMenuOpen(false); }}
    >
      {/* Desktop Icons */}
      {desktopIcons.map(icon => (
        <Win98DesktopIcon key={icon.id} {...icon} selected={selectedIcon === icon.id} onSelect={setSelectedIcon} onOpen={openWindow} />
      ))}

      {/* Windows */}
      <AnimatePresence>
        {Object.entries(windows).map(([id, window]) => (
          window.open && !window.minimized && (
            <Win98Window
              key={id} id={id} title={window.title} icon={window.icon}
              isActive={activeWindow === id} position={window.position} size={window.size}
              onClose={() => closeWindow(id)} onMinimize={() => minimizeWindow(id)} onFocus={() => focusWindow(id)}
              onDragEnd={(pos) => setWindows(prev => ({ ...prev, [id]: { ...prev[id], position: pos } }))}
            >
              {id === 'welcome' && <WelcomeContent onOpenContact={() => openWindow('contact')} onOpenServices={() => openWindow('services')} onClose={() => closeWindow('welcome')} />}
              {id === 'services' && <ServicesContent />}
              {id === 'about' && <AboutContent />}
              {id === 'contact' && <ContactContent />}
              {id === 'metrics' && <MetricsContent />}
              {id === 'appwizard' && <AppWizardContent onOpenContact={() => openWindow('contact')} onClose={() => closeWindow('appwizard')} />}
              {id === 'video' && <VideoContent />}
              {id === 'minesweeper' && <MinesweeperContent />}
              {id === 'help' && <HelpContent />}
              {['mycomputer', 'internet', 'documents', 'recycle'].includes(id) && (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666', fontFamily: 'Tahoma, sans-serif', fontSize: '11px' }}>
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>{window.icon}</div>
                  {id === 'mycomputer' && 'Viser systemressurser.'}
                  {id === 'internet' && 'Kobler til Internett...'}
                  {id === 'documents' && 'Mappen er tom.'}
                  {id === 'recycle' && 'Papirkurven er tom.'}
                </div>
              )}
            </Win98Window>
          )
        ))}
      </AnimatePresence>

      {/* Start Menu */}
      <AnimatePresence>
        {startMenuOpen && <Win98StartMenu isOpen={startMenuOpen} onClose={() => setStartMenuOpen(false)} onOpenWindow={openWindow} onShutdown={() => setIsShutdown(true)} />}
      </AnimatePresence>

      {/* Taskbar */}
      <Win98Taskbar openWindows={windows} activeWindow={activeWindow} onStartClick={() => setStartMenuOpen(!startMenuOpen)} startMenuOpen={startMenuOpen} onWindowClick={focusWindow} />

      <Toaster position="top-center" richColors />
    </div>
  );
}
